import {
  Box,
  Button,
  ButtonG4P,
  Checkbox,
  FileIcon,
  LocalizedString,
  Space,
  Stack,
  StatusLabel,
  useIsMobileLayout,
} from "@dbn/omnichannel-design-system-g4p";
import { option } from "fp-ts";
import { constFalse, pipe } from "fp-ts/function";
import { LocaleKey, RuntimeLocaleKey, useFormatMessage } from "../../intl";
import { PreContractualDocumentMeta } from "../documentAPI";
import { DocumentReadStatus, mediationInsuranceDocset } from "./utils";
import { PdfIcon } from "@g4p/react-styleguide-library";
import { mapAltTextKey } from "../DocumentViewer/DocumentViewer";
import { useAppContext } from "../../useAppContext";

export type DocumentReadingConfirmProps =
  | {
      readingConfirmationType: "none";
    }
  | {
      readingConfirmationType: "status";
      readStatus: DocumentReadStatus;
    }
  | {
      readingConfirmationType: "checkbox";
      isRead: boolean;
      onRead: () => unknown;
      onUnread: () => unknown;
      disabled: boolean;
    };

type Props = {
  document: PreContractualDocumentMeta;
  dataTestId: string;
  showErrors: boolean;
  onDocumentOpen: (document: PreContractualDocumentMeta) => unknown;
  isForeignClient?: boolean;
  onForeignDocumentOpen: (document: PreContractualDocumentMeta) => unknown;
  variant?: string;
} & DocumentReadingConfirmProps;

export function DocumentItem(props: Props) {
  const isMobileLayout = useIsMobileLayout();
  const formatMessage = useFormatMessage();
  const {
    apiParameters: { tenant },
  } = useAppContext();

  const rightContent = (() => {
    switch (props.readingConfirmationType) {
      case "none":
        return null;
      case "status":
        switch (props.readStatus) {
          case "Read":
            return (
              <StatusLabel
                status="completed"
                label={formatMessage("CreateProfileParent.status.done")}
              />
            );
          case "Reading":
            return (
              <StatusLabel
                status="uncompleted"
                label={formatMessage("CreateProfileParent.status.in.progress")}
              />
            );
          case "Unread":
            return (
              <StatusLabel
                status="undone"
                label={formatMessage("CreateProfileParent.status.following")}
              />
            );
        }
      case "checkbox":
        return (
          <Checkbox
            name={`read-document-${props.document.docId}`}
            label={option.some(
              pipe(
                props.document.docset,
                option.exists(item => item === mediationInsuranceDocset)
              )
                ? formatMessage(
                    "UKonto.ReadDocuments.Modal.checkbox.uKontoInsuranceMediation"
                  )
                : props.variant === "TermDeposit"
                ? formatMessage(
                    "TermDepositAccount.ReadDocuments.clientHasRead"
                  )
                : formatMessage("UKonto.ReadDocuments.clientHasRead")
            )}
            value={props.isRead}
            onChange={() => {
              if (props.isRead) {
                props.onUnread();
              } else {
                props.onDocumentOpen(props.document);
              }
            }}
            issuesType={
              props.showErrors && !props.isRead
                ? option.some("error")
                : option.none
            }
            disabled={props.disabled}
          />
        );
    }
  })();

  const stackProps = isMobileLayout
    ? {
        column: true,
        units: 6,
      }
    : ({
        column: false,
        fluid: true,
        vAlignContent: "center",
      } as
        | { column: boolean; units: number }
        | { column: boolean; fluid: true; vAlignContent: "center" });

  const isFrameWorkAgreement = (document: PreContractualDocumentMeta) =>
    pipe(
      document.docType,
      option.fold(constFalse, docType => docType === "FRAMEWORK_AGREEMENT")
    );

  const isProductContract = (document: PreContractualDocumentMeta) =>
    pipe(
      document.docType,
      option.fold(constFalse, docType => docType === "PRODUCT_CONTRACT_ACCOUNT")
    );

  const isInsuranceMediation = (document: PreContractualDocumentMeta) =>
    pipe(
      document.docType,
      option.fold(constFalse, docType => docType === "INSURANCE_MEDIATION")
    );

  const WrappedIcon = ({ size }: { size: "default" }) => {
    const iconSize = size === "default" ? 14 : 14;

    return <PdfIcon width={iconSize} height={iconSize} />;
  };

  const altTextKey = mapAltTextKey(props.document.docTitle);
  const altText =
    altTextKey !== ""
      ? formatMessage(altTextKey as RuntimeLocaleKey)
      : formatMessage(
          `documents.pdf.alt.${pipe(
            props.document.docType,
            option.getOrElse(() => "Document.signature.ready.alt")
          )}` as LocaleKey
        );

  return (
    <Stack
      {...stackProps}
      width={"100%"}
      data-test-id={props.dataTestId}
      style={{
        flexDirection: isMobileLayout ? "column" : "row",
        justifyContent: "space-between",
      }}
      grow
    >
      <Box column style={{ alignItems: "start" }}>
        <ButtonG4P
          variant="text"
          ariaLabel={altText}
          label={
            props.document.docTitle.toString().toUpperCase() as LocalizedString
          }
          labelAlign={"left"}
          icon={WrappedIcon}
          size="small"
          action={() => props.onDocumentOpen(props.document)}
          disabled={
            props.readingConfirmationType === "checkbox" && props.disabled
          }
        />
        {props.isForeignClient &&
          (isFrameWorkAgreement(props.document) ||
            isProductContract(props.document) ||
            (tenant === "CZ" && isInsuranceMediation(props.document))) && (
            <Box style={{ margin: "10px 0" }}>
              <Space units={4} />
              <Button
                variant="text"
                label={formatMessage(
                  "UKonto.ReadDocuments.foreignContractsLabel"
                )}
                ariaLabel={altText}
                labelAlign={"left"}
                icon={FileIcon}
                size="small"
                action={() => props.onForeignDocumentOpen(props.document)}
                disabled={
                  props.readingConfirmationType === "checkbox" && props.disabled
                }
              />
            </Box>
          )}
      </Box>
      <Stack column vAlignContent={"center"} hAlignContent={"left"}>
        <></>
        {rightContent}
      </Stack>
    </Stack>
  );
}
